import { cn } from '../lib/cn';

type Variant = 'dark' | 'light';

// Real Kalachand artwork, generated by scripts/brand/generate.mjs into public/.
const ICON = '/brand/icon.png'; // cocoa tile (white wordmark on #7A4A2B)
const WORDMARK_DARK = '/brand/wordmark-dark.png'; // black wordmark on transparent — for light surfaces
const WORDMARK_LIGHT = '/brand/wordmark-light.png'; // white wordmark on transparent — for dark surfaces

/**
 * The cocoa tile mark — for buttons, sidebar, app icons.
 * Reads on any background (the tile carries its own cocoa fill).
 * `variant` is kept for API compatibility but no longer changes the artwork.
 */
export function LogoMark({
  size = 32,
  className,
}: {
  size?: number;
  /** Kept for API compatibility; the cocoa tile reads on any background. */
  variant?: Variant;
  className?: string;
}): JSX.Element {
  return (
    <img
      src={ICON}
      width={size}
      height={size}
      alt="Kalachand"
      draggable={false}
      className={cn('shrink-0 select-none', className)}
    />
  );
}

/**
 * Horizontal wordmark lockup ("KALACHAND").
 * Used in the sidebar header, the login screen, and the verification page.
 * NOTE: the artwork PNGs in public/brand/ still render the old
 * "BEDDING WORLD" line — regenerate from new source art to fully retire it.
 * `variant="dark"` = black artwork (default, for light backgrounds);
 * `variant="light"` = white artwork (for dark backgrounds).
 */
export function Wordmark({
  size = 'md',
  variant = 'dark',
  className,
}: {
  size?: 'sm' | 'md' | 'lg';
  variant?: Variant;
  className?: string;
}): JSX.Element {
  const height = { sm: 24, md: 34, lg: 50 }[size];
  const src = variant === 'light' ? WORDMARK_LIGHT : WORDMARK_DARK;
  return (
    <img
      src={src}
      alt="Kalachand Mattress"
      draggable={false}
      style={{ height, width: 'auto' }}
      className={cn('select-none', className)}
    />
  );
}

/**
 * Stacked lockup — for tall surfaces (cover, splash, large hero).
 */
export function WordmarkStacked({
  size = 96,
  variant = 'dark',
  className,
}: {
  size?: number;
  variant?: Variant;
  className?: string;
}): JSX.Element {
  const src = variant === 'light' ? WORDMARK_LIGHT : WORDMARK_DARK;
  return (
    <div className={cn('inline-flex flex-col items-center gap-4', className)}>
      <LogoMark size={size} />
      <img
        src={src}
        alt="Kalachand Mattress"
        draggable={false}
        style={{ height: size * 0.32, width: 'auto' }}
        className="select-none"
      />
    </div>
  );
}
